import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { patientRequestsAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  User,
  Calendar,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Search,
  Filter,
  Eye,
  UserPlus,
  Stethoscope,
  FileText,
  RefreshCw,
  Link,
  AlertTriangle,
  UserCheck,
  Archive,
  FileSearch,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'New', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'reviewing', label: 'In Review', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'scheduled', label: 'Scheduled', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'converted', label: 'Converted', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  { value: 'archived', label: 'Archived', color: 'bg-slate-100 text-slate-800 border-slate-300' },
];

export default function PatientRequestsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  
  // Duplicate check state
  const [duplicateCheckOpen, setDuplicateCheckOpen] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [filterStatus]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const response = await patientRequestsAPI.getAll(params);
      // Sort by submitted_at descending (newest first)
      const sortedRequests = (response.data || []).sort((a, b) => 
        new Date(b.submitted_at) - new Date(a.submitted_at)
      );
      setRequests(sortedRequests);
    } catch (err) {
      toast.error('Failed to load patient requests');
    }
    setLoading(false);
  };

  const updateStatus = async (requestId, newStatus) => {
    setUpdating(true);
    try {
      await patientRequestsAPI.updateStatus(requestId, newStatus, reviewNotes);
      toast.success(`Request status updated to ${newStatus}`);
      loadRequests();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
      setReviewNotes('');
    } catch (err) {
      toast.error('Failed to update status');
    }
    setUpdating(false);
  };

  const handleConvertClick = async (request) => {
    setSelectedRequest(request);
    setCheckingDuplicates(true);
    setDuplicateCheckOpen(true);
    
    try {
      const response = await patientRequestsAPI.checkDuplicates(request.id);
      setDuplicateData(response.data);
    } catch (err) {
      toast.error('Failed to check for duplicates');
      setDuplicateCheckOpen(false);
    }
    setCheckingDuplicates(false);
  };

  const handleLinkToExisting = async (patientId) => {
    setUpdating(true);
    try {
      const response = await patientRequestsAPI.convertToPatient(selectedRequest.id, {
        link_to_patient_id: patientId
      });
      
      toast.success(response.data.message || 'Request linked to existing patient');
      setDuplicateCheckOpen(false);
      setDetailsOpen(false);
      loadRequests();
      
      // Navigate to patient profile
      navigate(`/app/patients/${patientId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link to patient');
    }
    setUpdating(false);
  };

  const handleCreateNewAnyway = async () => {
    setUpdating(true);
    try {
      const response = await patientRequestsAPI.convertToPatient(selectedRequest.id, {
        force_create: true
      });
      
      toast.success(response.data.message || 'Patient created successfully');
      setDuplicateCheckOpen(false);
      setDetailsOpen(false);
      loadRequests();
      
      // Navigate to new patient profile
      if (response.data.patient_id) {
        navigate(`/app/patients/${response.data.patient_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create patient');
    }
    setUpdating(false);
  };

  const handleDirectConvert = async () => {
    // Called when no duplicates found
    setUpdating(true);
    try {
      const response = await patientRequestsAPI.convertToPatient(selectedRequest.id, {
        force_create: true
      });
      
      toast.success(response.data.message || 'Patient created successfully');
      setDuplicateCheckOpen(false);
      setDetailsOpen(false);
      loadRequests();
      
      // Navigate to new patient profile
      if (response.data.patient_id) {
        navigate(`/app/patients/${response.data.patient_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create patient');
    }
    setUpdating(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return (
      <Badge className={`${statusConfig.color} border`} variant="outline">
        {statusConfig.label}
      </Badge>
    );
  };

  const getMatchReasonLabel = (reason) => {
    switch (reason) {
      case 'phone_match': return 'Phone Number';
      case 'email_match': return 'Email Address';
      case 'name_dob_match': return 'Name + Date of Birth';
      default: return reason;
    }
  };

  const filteredRequests = requests.filter(req => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      req.first_name?.toLowerCase().includes(query) ||
      req.last_name?.toLowerCase().includes(query) ||
      req.email?.toLowerCase().includes(query) ||
      req.phone?.toLowerCase().includes(query) ||
      req.reference_number?.toLowerCase().includes(query) ||
      req.chief_complaint?.toLowerCase().includes(query)
    );
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const reviewingCount = requests.filter(r => r.status === 'reviewing').length;
  const convertedCount = requests.filter(r => r.status === 'converted').length;

  // Access control
  if (!user || !['admin', 'physician'].includes(user.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patient-requests-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Patient Requests</h1>
          <p className="text-slate-500">
            Review and convert incoming patient appointment requests
          </p>
        </div>
        <Button onClick={loadRequests} variant="outline" disabled={loading} data-testid="refresh-requests-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="premium-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="pending-count">{pendingCount}</p>
                <p className="text-xs text-slate-500">New</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="premium-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileSearch className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="reviewing-count">{reviewingCount}</p>
                <p className="text-xs text-slate-500">In Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="premium-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <UserPlus className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="converted-count">{convertedCount}</p>
                <p className="text-xs text-slate-500">Converted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="premium-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <FileText className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-count">{requests.length}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="premium-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                placeholder="Search by name, email, phone, reference..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-requests-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-status-select">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Requests</SelectItem>
                {STATUS_OPTIONS.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card className="premium-card">
        <CardHeader>
          <CardTitle>Appointment Requests</CardTitle>
          <CardDescription>
            {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No patient requests found</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Visit Type</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow key={request.id} data-testid={`request-row-${request.id}`}>
                      <TableCell className="font-mono text-sm">{request.reference_number}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{request.first_name} {request.last_name}</p>
                          <p className="text-xs text-slate-500">DOB: {request.date_of_birth}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="flex items-center gap-1">
                            <Phone className="h-3 w-3 text-slate-400" /> {request.phone}
                          </p>
                          <p className="flex items-center gap-1 text-xs text-slate-500">
                            <Mail className="h-3 w-3" /> {request.email}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{request.visit_type}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatDateTime(request.submitted_at)}
                      </TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedRequest(request);
                            setDetailsOpen(true);
                          }}
                          data-testid={`view-request-${request.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Request Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedRequest && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>Patient Request Details</DialogTitle>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <DialogDescription>
                  Reference: {selectedRequest.reference_number}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                {/* Patient Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500 text-xs">Patient Name</Label>
                    <p className="font-medium">{selectedRequest.first_name} {selectedRequest.last_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Date of Birth</Label>
                    <p>{selectedRequest.date_of_birth}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Email</Label>
                    <p className="flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {selectedRequest.email}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Phone</Label>
                    <p className="flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {selectedRequest.phone}
                    </p>
                  </div>
                </div>

                {/* Visit Info */}
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Stethoscope className="h-4 w-4" />
                    Visit Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-slate-500 text-xs">Visit Type</Label>
                      <p className="font-medium">{selectedRequest.visit_type}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Preferred Date</Label>
                      <p>{selectedRequest.preferred_date || 'Flexible'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Preferred Time</Label>
                      <p>{selectedRequest.preferred_time || 'Flexible'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Symptoms Duration</Label>
                      <p>{selectedRequest.symptoms_duration || 'Not specified'}</p>
                    </div>
                  </div>
                </div>

                {/* Chief Complaint */}
                <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
                  <h4 className="font-semibold mb-2 text-red-800 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Chief Complaint
                  </h4>
                  <p className="text-red-900">{selectedRequest.chief_complaint}</p>
                </div>

                {/* Medical History */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500 text-xs">Current Medications</Label>
                    <p className="text-sm">{selectedRequest.current_medications || 'None reported'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Allergies</Label>
                    <p className="text-sm">{selectedRequest.allergies || 'None reported'}</p>
                  </div>
                </div>

                {/* Insurance */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500 text-xs">Insurance Provider</Label>
                    <p className="text-sm">{selectedRequest.insurance_provider || 'Not provided'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Member ID</Label>
                    <p className="text-sm">{selectedRequest.insurance_id || 'Not provided'}</p>
                  </div>
                </div>

                {/* Additional Notes */}
                {selectedRequest.additional_notes && (
                  <div>
                    <Label className="text-slate-500 text-xs">Additional Notes</Label>
                    <p className="text-sm p-3 bg-muted rounded-lg">{selectedRequest.additional_notes}</p>
                  </div>
                )}

                {/* Conversion Info (if converted) */}
                {selectedRequest.status === 'converted' && (
                  <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg">
                    <h4 className="font-semibold mb-2 text-purple-800 flex items-center gap-2">
                      <UserCheck className="h-4 w-4" />
                      Conversion Details
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-purple-600">Converted by:</span>{' '}
                        {selectedRequest.converted_by_name}
                      </div>
                      <div>
                        <span className="text-purple-600">Converted at:</span>{' '}
                        {formatDateTime(selectedRequest.converted_at)}
                      </div>
                      <div className="col-span-2">
                        <span className="text-purple-600">Type:</span>{' '}
                        {selectedRequest.conversion_type === 'linked' ? 'Linked to existing patient' : 'New patient created'}
                      </div>
                    </div>
                    {selectedRequest.converted_patient_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => {
                          setDetailsOpen(false);
                          navigate(`/app/patients/${selectedRequest.converted_patient_id}`);
                        }}
                        data-testid="view-converted-patient-btn"
                      >
                        <User className="h-4 w-4 mr-1" />
                        View Patient Profile
                      </Button>
                    )}
                  </div>
                )}

                {/* Review Notes (for non-converted requests) */}
                {selectedRequest.status !== 'converted' && (
                  <div>
                    <Label htmlFor="review_notes">Review Notes</Label>
                    <Textarea
                      id="review_notes"
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add notes about this request..."
                      rows={2}
                    />
                  </div>
                )}

                {/* Metadata */}
                <div className="text-xs text-slate-500 border-t pt-4">
                  <p>Submitted: {formatDateTime(selectedRequest.submitted_at)}</p>
                  {selectedRequest.reviewed_by_name && (
                    <p>Reviewed by: {selectedRequest.reviewed_by_name}</p>
                  )}
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                {selectedRequest.status !== 'converted' && (
                  <>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        onClick={() => updateStatus(selectedRequest.id, 'reviewing')}
                        disabled={updating || selectedRequest.status === 'reviewing'}
                        size="sm"
                      >
                        <FileSearch className="h-4 w-4 mr-1" />
                        Mark In Review
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => updateStatus(selectedRequest.id, 'archived')}
                        disabled={updating}
                        size="sm"
                        className="text-slate-600"
                      >
                        <Archive className="h-4 w-4 mr-1" />
                        Archive
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => updateStatus(selectedRequest.id, 'declined')}
                        disabled={updating}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Decline
                      </Button>
                      <Button
                        onClick={() => handleConvertClick(selectedRequest)}
                        disabled={updating}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="convert-to-patient-btn"
                      >
                        {updating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}
                        Convert to Patient
                      </Button>
                    </div>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate Check / Conversion Dialog */}
      <Dialog open={duplicateCheckOpen} onOpenChange={setDuplicateCheckOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {checkingDuplicates ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Checking for Duplicates...
                </>
              ) : duplicateData?.has_duplicates ? (
                <>
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Potential Duplicate Found
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  No Duplicates Found
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {checkingDuplicates 
                ? 'Searching for existing patients with matching information...'
                : duplicateData?.has_duplicates
                  ? 'We found a patient that might match this request. Please review and choose an action.'
                  : 'You can safely create a new patient record.'
              }
            </DialogDescription>
          </DialogHeader>

          {!checkingDuplicates && duplicateData && (
            <div className="space-y-4">
              {duplicateData.has_duplicates ? (
                <>
                  {/* Show duplicate matches */}
                  <div className="space-y-3">
                    {duplicateData.duplicates.map((patient, idx) => {
                      const matchReason = duplicateData.match_reasons.find(
                        r => r.patient_id === patient.id
                      );
                      return (
                        <div 
                          key={patient.id} 
                          className="p-4 border rounded-lg bg-amber-50 border-amber-200"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-amber-900">
                                {patient.first_name} {patient.last_name}
                              </p>
                              <p className="text-sm text-amber-700">
                                Patient ID: {patient.patient_id || patient.id.slice(0, 8)}
                              </p>
                              <div className="mt-2 text-sm text-amber-800">
                                <p><span className="font-medium">DOB:</span> {patient.date_of_birth}</p>
                                {patient.phone && <p><span className="font-medium">Phone:</span> {patient.phone}</p>}
                                {patient.email && <p><span className="font-medium">Email:</span> {patient.email}</p>}
                              </div>
                              {matchReason && (
                                <Badge className="mt-2 bg-amber-100 text-amber-800 border-amber-300" variant="outline">
                                  Match: {getMatchReasonLabel(matchReason.reason)}
                                </Badge>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleLinkToExisting(patient.id)}
                              disabled={updating}
                              className="shrink-0"
                              data-testid={`link-to-patient-${patient.id}`}
                            >
                              {updating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Link className="h-4 w-4 mr-1" />
                                  Link to This Patient
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-muted-foreground">Or</span>
                    </div>
                  </div>

                  {/* Create new anyway option */}
                  <div className="p-4 border rounded-lg bg-slate-50">
                    <p className="text-sm text-slate-600 mb-3">
                      If you're sure this is a different person, you can create a new patient record anyway.
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleCreateNewAnyway}
                      disabled={updating}
                      data-testid="create-new-anyway-btn"
                    >
                      {updating ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <UserPlus className="h-4 w-4 mr-1" />
                      )}
                      Create New Patient Anyway
                    </Button>
                  </div>
                </>
              ) : (
                /* No duplicates - proceed with conversion */
                <div className="p-4 border rounded-lg bg-green-50 border-green-200">
                  <p className="text-sm text-green-700 mb-3">
                    No existing patients match this request. A new patient record will be created with the following information:
                  </p>
                  <div className="text-sm space-y-1 text-green-800">
                    <p><span className="font-medium">Name:</span> {selectedRequest?.first_name} {selectedRequest?.last_name}</p>
                    <p><span className="font-medium">DOB:</span> {selectedRequest?.date_of_birth}</p>
                    <p><span className="font-medium">Phone:</span> {selectedRequest?.phone}</p>
                    <p><span className="font-medium">Email:</span> {selectedRequest?.email}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateCheckOpen(false)}>
              Cancel
            </Button>
            {!checkingDuplicates && !duplicateData?.has_duplicates && (
              <Button 
                onClick={handleDirectConvert}
                disabled={updating}
                data-testid="confirm-convert-btn"
              >
                {updating ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4 mr-1" />
                )}
                Create Patient
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
